import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram, Send } from "lucide-react";
import { toast } from "sonner";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";
import { INSTAGRAM_HANDLE, INSTAGRAM_URL } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subjects = [
    "Recital o lectura",
    "Publicaciones y libros",
    "Vídeo poesía / colaboración",
    "Prensa",
    "Otro",
];

const inputClass =
    "w-full bg-transparent border-b border-ink/50 focus:border-crimson outline-none py-3 text-lg text-ink placeholder:text-subtle transition-colors";

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", subject: subjects[0], message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSent(true);
            toast.success("Tu carta ha llegado. Gracias por escribir.");
        } catch {
            toast.error("No se pudo enviar. Inténtalo de nuevo en un momento.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contacto" data-testid="contact-section" className="relative py-24 sm:py-36 overflow-hidden">
            <img
                src="/media/trazo-luna.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute right-4 sm:right-16 top-10 w-40 sm:w-56 opacity-[0.1] rotate-[-8deg]"
            />
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <SectionHeading
                    kicker="06 — Correspondencia"
                    title="Escríbeme unas líneas"
                    description="Recitales, lecturas, publicaciones o colaboraciones de vídeo poesía. Todo mensaje se lee y se responde."
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
                    <Reveal className="lg:col-span-5">
                        <div className="space-y-8">
                            <p className="font-display italic text-2xl leading-snug text-ink/80 max-w-sm">
                                «Para ese último día en esta extraña vida; solo pido
                                que me acompañen, pero jamás se despidan.»
                            </p>
                            <div className="space-y-4 font-mono text-xs uppercase tracking-[0.22em] text-subtle">
                                <p>Quito, Ecuador</p>
                                <a
                                    data-testid="contact-instagram-link"
                                    href={INSTAGRAM_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 text-ink hover:text-crimson transition-colors"
                                >
                                    <Instagram size={15} />
                                    {INSTAGRAM_HANDLE}
                                </a>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15} className="lg:col-span-7">
                        {sent ? (
                            <motion.div
                                data-testid="contact-form-success"
                                className="border border-line bg-cream p-10 sm:p-14"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-crimson">
                                    Carta recibida
                                </p>
                                <h3 className="mt-4 font-display text-3xl sm:text-4xl text-ink">
                                    Gracias por escribir, {form.name.split(" ")[0]}.
                                </h3>
                                <p className="mt-4 text-lg text-ink/75 max-w-md leading-relaxed">
                                    Tu mensaje ya está en mi escritorio. Te responderé
                                    pronto a <span className="italic">{form.email}</span>.
                                </p>
                                <img
                                    src="/media/trazo-firma.png"
                                    alt="Firma de José Luis Garcés Maldonado"
                                    className="mt-6 h-14 w-auto opacity-80"
                                />
                                <button
                                    data-testid="contact-form-another-button"
                                    onClick={() => {
                                        setSent(false);
                                        setForm({ name: "", email: "", subject: subjects[0], message: "" });
                                    }}
                                    className="mt-8 border-b border-ink pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-ink hover:text-crimson hover:border-crimson transition-colors"
                                >
                                    Enviar otra carta
                                </button>
                            </motion.div>
                        ) : (
                            <form
                                data-testid="contact-form"
                                onSubmit={submit}
                                className="border border-line bg-paper p-8 sm:p-12 shadow-sm"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">
                                            Nombre
                                        </span>
                                        <input
                                            data-testid="contact-form-name-input"
                                            required
                                            minLength={2}
                                            value={form.name}
                                            onChange={set("name")}
                                            placeholder="Tu nombre"
                                            className={inputClass}
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">
                                            Correo
                                        </span>
                                        <input
                                            data-testid="contact-form-email-input"
                                            required
                                            type="email"
                                            value={form.email}
                                            onChange={set("email")}
                                            placeholder="tu@correo.com"
                                            className={inputClass}
                                        />
                                    </label>
                                </div>
                                <label className="block mt-8">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">
                                        Asunto
                                    </span>
                                    <select
                                        data-testid="contact-form-subject-select"
                                        value={form.subject}
                                        onChange={set("subject")}
                                        className={`${inputClass} cursor-pointer`}
                                    >
                                        {subjects.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block mt-8">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">
                                        Mensaje
                                    </span>
                                    <textarea
                                        data-testid="contact-form-message-input"
                                        required
                                        minLength={10}
                                        rows={5}
                                        value={form.message}
                                        onChange={set("message")}
                                        placeholder="Escríbeme unas líneas…"
                                        className={`${inputClass} resize-none`}
                                    />
                                </label>
                                <button
                                    data-testid="contact-form-submit-button"
                                    type="submit"
                                    disabled={sending}
                                    className="mt-10 group flex items-center gap-3 bg-ink text-paper px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-crimson disabled:opacity-50 transition-colors duration-300"
                                >
                                    {sending ? "Enviando…" : "Enviar mensaje"}
                                    <Send size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </button>
                                <div className="mt-10 flex flex-col items-end border-t border-line pt-6">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">
                                        Siempre te responderé
                                    </p>
                                    <img
                                        data-testid="contact-signature"
                                        src="/media/trazo-firma.png"
                                        alt="Firma de José Luis Garcés Maldonado"
                                        className="mt-3 h-16 w-auto opacity-80"
                                    />
                                </div>
                            </form>
                        )}
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default Contact;
